import React, { useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  LineController,
  Tooltip,
  Legend,
  ChartOptions,
} from 'chart.js';
import { SparklineData, LOEStatus } from '../../../../../../useProductAnalysis.logic';
import './SparklinePanel.styles.scss';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  LineController,
  Tooltip,
  Legend
);

interface SparklinePanelProps {
  sparklineData: SparklineData;
  loeStatus: LOEStatus;
  periodMonths?: number; // Number of months for x-axis (3, 6, 12, or custom)
}

/**
 * Format a value based on a fixed scale unit (K, M, or B)
 * This ensures all values on an axis use the same unit for consistency
 */
const formatValueWithUnit = (value: number, unit: 'B' | 'M' | 'K' | ''): string => {
  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';

  switch (unit) {
    case 'B':
      return sign + (absValue / 1_000_000_000).toFixed(1) + 'B';
    case 'M':
      return sign + (absValue / 1_000_000).toFixed(1) + 'M';
    case 'K':
      return sign + (absValue / 1_000).toFixed(1) + 'K';
    default:
      return sign + absValue.toLocaleString();
  }
};

/**
 * Get the scale unit info based on the data range
 * Returns both the label for axis title and the abbreviation key for legend
 */
const getScaleInfo = (
  dataArray: number[]
): { label: string; abbreviation: string; description: string } => {
  if (!dataArray || dataArray.length === 0) return { label: '', abbreviation: '', description: '' };
  const maxValue = Math.max(...dataArray.filter(v => v !== null && v !== undefined));
  if (maxValue >= 1_000_000_000)
    return { label: '(in Billions)', abbreviation: 'B', description: 'Billions' };
  if (maxValue >= 1_000_000)
    return { label: '(in Millions)', abbreviation: 'M', description: 'Millions' };
  if (maxValue >= 1_000)
    return { label: '(in Thousands)', abbreviation: 'K', description: 'Thousands' };
  return { label: '', abbreviation: '', description: '' };
};

const SparklinePanel: React.FC<SparklinePanelProps> = ({
  sparklineData,
  loeStatus: _loeStatus,
  periodMonths = 3,
}) => {
  // Generate x-axis labels based on periodMonths only
  // Labels will range from 1 to periodMonths (e.g., 1-3 for 3 months, 1-6 for 6 months)
  const xAxisLabels = useMemo(() => {
    return Array.from({ length: periodMonths }, (_, index) => index + 1);
  }, [periodMonths]);

  // Combined chart data with both sales and volume on dual y-axes
  // Using official brand colors: Teal for Sales, Gold for Volume
  const combinedChartData = useMemo(() => {
    return {
      labels: xAxisLabels,
      datasets: [
        {
          label: 'Government Sales ($)',
          data: sparklineData.salesTrend,
          borderColor: '#029999', // Brand Teal
          backgroundColor: 'rgba(2, 153, 153, 0.1)',
          borderWidth: 2,
          fill: false,
          tension: 0.4,
          pointRadius: 3,
          pointHoverRadius: 5,
          pointBackgroundColor: '#029999', // Brand Teal
          yAxisID: 'y-sales',
        },
        {
          label: 'Volume (Units)',
          data: sparklineData.volumeTrend,
          borderColor: '#C79213', // Brand Gold (Pantone 1245 C)
          backgroundColor: 'rgba(199, 146, 19, 0.1)',
          borderWidth: 2,
          fill: false,
          tension: 0.4,
          pointRadius: 3,
          pointHoverRadius: 5,
          pointBackgroundColor: '#C79213', // Brand Gold
          yAxisID: 'y-volume',
        },
      ],
    };
  }, [sparklineData.salesTrend, sparklineData.volumeTrend, xAxisLabels]);

  // Get scale info for the axes (label, abbreviation, description)
  const salesScaleInfo = useMemo(
    () => getScaleInfo(sparklineData.salesTrend),
    [sparklineData.salesTrend]
  );
  const volumeScaleInfo = useMemo(
    () => getScaleInfo(sparklineData.volumeTrend),
    [sparklineData.volumeTrend]
  );

  // Combined chart options with dual y-axes
  const combinedChartOptions: ChartOptions<'line'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index',
        intersect: false,
      },
      layout: {
        padding: {
          top: 10,
          right: 15,
          bottom: 10,
          left: 15,
        },
      },
      plugins: {
        legend: {
          display: true,
          position: 'top',
          align: 'center',
          labels: {
            usePointStyle: true,
            pointStyle: 'circle',
            padding: 15,
            font: {
              size: 15,
            },
            color: '#374151',
          },
        },
        tooltip: {
          enabled: true,
          mode: 'index',
          intersect: false,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          titleColor: '#374151',
          bodyColor: '#374151',
          borderColor: '#e5e7eb',
          borderWidth: 1,
          padding: 12,
          callbacks: {
            label: function (context) {
              const label = context.dataset.label || '';
              const value = context.parsed.y;
              if (context.datasetIndex === 0) {
                // Sales dataset - show full formatted value in tooltip
                return `${label}: $${Number(value).toLocaleString()}`;
              } else {
                // Volume dataset - show full formatted value in tooltip
                return `${label}: ${Number(value).toLocaleString()} units`;
              }
            },
          },
        },
      },
      scales: {
        x: {
          display: true,
          type: 'category',
          title: {
            display: true,
            text: 'Period (Months)',
            color: '#6b7280',
            font: {
              size: 14,
              weight: 'normal',
            },
          },
          ticks: {
            color: '#6b7280',
            font: {
              size: 15,
            },
            autoSkip: false,
            maxRotation: 0,
          },
          grid: {
            display: true,
            color: 'rgba(107, 114, 128, 0.1)',
          },
        },
        'y-sales': {
          type: 'linear',
          display: true,
          position: 'left',
          title: {
            display: true,
            text: salesScaleInfo.label ? `Sales ($) ${salesScaleInfo.label}` : 'Sales ($)',
            color: '#029999', // Brand Teal
            font: {
              size: 14,
              weight: 'bold',
            },
          },
          ticks: {
            color: '#029999', // Brand Teal
            font: {
              size: 15,
            },
            maxTicksLimit: 8,
            callback: function (value) {
              // Use consistent unit based on the data range
              const unit = salesScaleInfo.abbreviation as 'B' | 'M' | 'K' | '';
              return '$' + formatValueWithUnit(Number(value), unit);
            },
          },
          grid: {
            display: true,
            color: 'rgba(2, 153, 153, 0.1)', // Brand Teal with opacity
          },
          beginAtZero: true,
        },
        'y-volume': {
          type: 'linear',
          display: true,
          position: 'right',
          title: {
            display: true,
            text: volumeScaleInfo.label
              ? `Volume (Units) ${volumeScaleInfo.label}`
              : 'Volume (Units)',
            color: '#C79213', // Brand Gold
            font: {
              size: 14,
              weight: 'bold',
            },
          },
          ticks: {
            color: '#C79213', // Brand Gold
            font: {
              size: 15,
            },
            maxTicksLimit: 8,
            callback: function (value) {
              // Use consistent unit based on the data range
              const unit = volumeScaleInfo.abbreviation as 'B' | 'M' | 'K' | '';
              return formatValueWithUnit(Number(value), unit);
            },
          },
          grid: {
            drawOnChartArea: false, // Don't draw grid lines for right axis to avoid clutter
          },
          beginAtZero: true,
        },
      },
      elements: {
        line: {
          borderJoinStyle: 'round' as const,
        },
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      periodMonths,
      salesScaleInfo.label,
      salesScaleInfo.abbreviation,
      volumeScaleInfo.label,
      volumeScaleInfo.abbreviation,
    ]
  );

  return (
    <div className="sparkline-panel">
      <div className="sparkline-header">
        <h4 className="sparkline-title">Government Sales & Volume Trends</h4>
      </div>
      <div className="sparkline-item combined-chart">
        <div className="sparkline-chart">
          <Line
            key={`combined-chart-${periodMonths}`}
            data={combinedChartData}
            options={combinedChartOptions}
          />
        </div>
      </div>
      {/* <div className="loe-status">
        <span className={`loe-badge ${loeStatus.badgeType}`}>
          <span className="loe-badge-text">{loeStatus.status}</span>
        </span>
        <small>
          <span className="loe-status-description">{loeStatus.description}</span>
        </small>
      </div> */}
    </div>
  );
};

export default SparklinePanel;
